import { useRef, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../data";
import { maskLine, EASE, scrollTo } from "../../lib/anim";

const HEAD_LINES = ["L'aviazione è il nostro", "punto di partenza.", "La visione è ciò che costruiamo."];

const Particles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 26 }).map(() => ({
        left: Math.random() * 100,
        top: 40 + Math.random() * 60,
        size: 1 + Math.random() * 2.5,
        dur: 9 + Math.random() * 12,
        delay: Math.random() * 10,
      })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((d, i) => (
        <span
          key={i}
          className="particle"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            animationDuration: `${d.dur}s`,
            animationDelay: `${d.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale, transformOrigin: "95% 45%" }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Aereo in decollo — MGB Aviation" title="MGB Aviation" draggable="false" className="h-full w-full object-cover object-[96%_center] md:object-[78%_center]" />
      </motion.div>

      {/* overlays */}
      <div className="absolute inset-0 bg-[#060607]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#060607] via-[#060607]/25 to-[#060607]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#060607]/70 via-transparent to-transparent" />
      <Particles />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 h-full max-w-[1500px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-[10vh]"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="mb-8"
        />

        <h1 className="font-display font-semibold text-white leading-[0.95] tracking-tight text-[13vw] sm:text-[9vw] lg:text-[6.6vw] max-w-[16ch]">
          {HEAD_LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={maskLine}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed"
        >
          MGB Aviation è la realtà dietro una collezione di brand indipendenti ispirati ai valori dell'aviazione. Ogni progetto nasce da una stessa filosofia: precisione, disciplina, affidabilità e visione a lungo termine.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.05 }}
          className="mt-10"
        >
          <button
            onClick={() => scrollTo("ecosystem")}
            data-testid="hero-cta"
            className="group relative inline-flex items-center gap-4 border border-white/25 pl-7 pr-6 py-4 overflow-hidden"
          >
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative font-mono-x text-[12px] tracking-[0.22em] uppercase text-white group-hover:text-[#060607] transition-colors duration-500">
              Scopri i nostri brand
            </span>
            <ArrowDown size={15} className="relative text-white group-hover:text-[#060607] transition-colors duration-500" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-6 lg:right-12 z-10 flex items-center gap-3"
      >
        <span className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-white/40">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block h-8 w-px bg-white/40"
        />
      </motion.div>
    </section>
  );
};
